/**
 * imageEditor – client-side crop + rotation for the Farmly cover image editor.
 *
 * Takes a source image (File or URL), applies rotation and crop,
 * and exports a new Blob/File ready for the upload pipeline.
 *
 * No external dependencies — uses Canvas API directly.
 */

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Applies rotation and crop to an image, returning a JPEG Blob.
 *
 * @param imageSrc – object URL or data URL of the source image
 * @param crop – pixel crop area (from react-easy-crop onCropComplete)
 * @param rotation – degrees (0, 90, 180, 270)
 * @param quality – JPEG quality 0–1 (default 0.92)
 */
export async function exportEditedImage(
  imageSrc: string,
  crop: CropArea,
  rotation: number = 0,
  quality: number = 0.92,
): Promise<Blob> {
  const image = await loadImage(imageSrc);

  // 1. Create a canvas with the rotated full image
  const rotatedCanvas = rotateImage(image, rotation);

  // 2. Crop from the rotated canvas
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = crop.width;
  croppedCanvas.height = crop.height;
  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');

  ctx.drawImage(
    rotatedCanvas,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas export failed'));
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Wraps exportEditedImage and returns a File with a name derived from the original.
 */
export async function exportEditedFile(
  imageSrc: string,
  crop: CropArea,
  rotation: number,
  originalFileName: string,
  quality?: number,
): Promise<File> {
  const blob = await exportEditedImage(imageSrc, crop, rotation, quality);
  const baseName = originalFileName.replace(/\.[^.]+$/, '');
  const name = `${baseName}-edited.jpg`;
  return new File([blob], name, { type: 'image/jpeg' });
}

/* ── Helpers ── */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image for editing'));
    img.src = src;
  });
}

function rotateImage(image: HTMLImageElement, degrees: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');

  const rad = (degrees * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));

  // Rotated bounding box
  canvas.width = Math.floor(image.naturalWidth * cos + image.naturalHeight * sin);
  canvas.height = Math.floor(image.naturalWidth * sin + image.naturalHeight * cos);

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  return canvas;
}
